/**
 * Auth token storage — session-scoped, defense-in-depth.
 *
 * WHY sessionStorage vs localStorage:
 *   - localStorage persists across tabs, browser restarts, and is a prime XSS
 *     target (a single injected script can exfiltrate the token for days).
 *   - sessionStorage is scoped to the tab, cleared on close, invisible to other
 *     tabs of the same origin, and shortens the attack window considerably.
 *
 * WHY not httpOnly cookies yet (proper fix):
 *   - Requires backend refactor (Set-Cookie on /auth/login+register+refresh,
 *     Clear-Cookie on /auth/logout), CORS re-config with allow_credentials=True
 *     and an allow-list of origins (no wildcard), CSRF-token flow, and removal
 *     of every `Authorization: Bearer` header across ~20 frontend files.
 *   - Tracked as follow-up P1 work in /app/memory/PRD.md.
 *
 * If you're touching this file, do NOT reintroduce localStorage.
 */
const KEY = 'hp-user-token';

const store = () => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch (e) {
    // Storage access can throw in certain iframe / privacy contexts.
    console.debug('[auth] sessionStorage unavailable:', e.name);
    return null;
  }
};

export const getToken = () => {
  const s = store();
  if (!s) return null;
  try {
    return s.getItem(KEY);
  } catch (e) {
    console.debug('[auth] getItem failed:', e.name);
    return null;
  }
};

export const setToken = (token) => {
  const s = store();
  if (!s) return;
  try { s.setItem(KEY, token); } catch (e) { console.debug('[auth] token persist failed:', e.name); }
  // One-time cleanup: purge any legacy localStorage token from previous versions.
  try {
    window.localStorage.removeItem(KEY);
  } catch (e) {
    // localStorage can be blocked in private mode — safe to ignore.
    console.debug('[auth] legacy localStorage cleanup skipped:', e.name);
  }
};

export const clearToken = () => {
  const s = store();
  try {
    s && s.removeItem(KEY);
  } catch (e) {
    console.debug('[auth] sessionStorage clear failed:', e.name);
  }
  try {
    window.localStorage.removeItem(KEY);
  } catch (e) {
    console.debug('[auth] legacy localStorage clear failed:', e.name);
  }
};
